import { useState } from "react";
import { useTranslations } from "next-intl";
import { Guest } from "@/types/Guest";
import { clearEmailUndeliverable } from "@/lib/api/clients/GuestClient";
import { useToast } from "@/components/Toast/Toast";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";

type EmailUndeliverableNoticeProps = {
  guest?: Guest;
  /** Falls back to `guest.eventId`, which list-loaded guests do not always carry. */
  eventId?: string;
};

/**
 * PB-TH-7 — the guest's address bounced, so nothing is being sent to them.
 *
 * A hard bounce sets `emailUndeliverable`, and main-consumer checks it BEFORE
 * the consent gate: while it stands the guest receives NOTHING — not a
 * newsletter, not a ticket, not a confirmation. Until this existed there was no
 * reader and no way back, so one mistyped address suppressed a guest for ever,
 * invisibly, and the documented cure was a hand-written database update.
 *
 * Stated in words rather than as a second on/off switch beside the opt-in,
 * because the two mean different things and need different fixes: opted out is
 * the guest's decision to respect, this is a dead address to correct.
 *
 * Deliberately rendered OUTSIDE the customizable guest-detail layout. Every
 * field in that layout can be hidden by an organiser editing it; a suppression
 * that silences a guest's tickets must not be something the UI can be
 * configured to conceal.
 */
export const EmailUndeliverableNotice: React.FC<EmailUndeliverableNoticeProps> = ({
  guest,
  eventId,
}) => {
  const t = useTranslations("guest-details");
  const { showToast } = useToast();
  const [clearing, setClearing] = useState(false);
  // The guest object is owned by the list/fetch above this component, so the
  // banner hides itself on success instead of pretending to own that state.
  // A refetch re-derives it from the server either way.
  const [clearedLocally, setClearedLocally] = useState(false);

  const resolvedEventId = eventId || guest?.eventId;
  if (guest?.emailUndeliverable !== true || clearedLocally) return null;

  const handleClear = async () => {
    if (!resolvedEventId || !guest?.guestId || clearing) return;
    setClearing(true);
    try {
      const result = await clearEmailUndeliverable(resolvedEventId, guest.guestId);
      if (result.pending) {
        // The address still has to come off the ACCOUNT-level SES suppression
        // list, and only once that succeeds is the guest genuinely reachable.
        // So the banner STAYS: hiding it here is what the old flow did, and it
        // reported a success that had not happened yet and sometimes never did.
        // The detail panel's own refetch takes it down when the work lands.
        showToast(t("undeliverableReleaseRequested"), "info");
        return;
      }
      // `cleared:false` means it was already deliverable. Saying so plainly
      // matters: the organiser is here because mail is missing, and if the
      // bounce was not the cause they need to keep looking rather than leave
      // believing it is handled.
      showToast(
        result.cleared ? t("undeliverableCleared") : t("undeliverableWasNotSet"),
        result.cleared ? "success" : "info",
      );
      setClearedLocally(true);
    } catch {
      showToast(t("undeliverableClearFailed"), "error");
    } finally {
      setClearing(false);
    }
  };

  return (
    // `@container` measures THIS box, not the viewport, and it has to: the
    // notice sits in the guest-detail panel, which from `md` up shares its row
    // with a fixed 360px notes column inside a capped popup. At a 768px
    // viewport that leaves the notice roughly 240px — so a viewport-keyed
    // `sm:flex-row` put the row side-by-side exactly where there was no room
    // for it. Scoped to a wrapper rather than the panel above because
    // `container-type` also makes the element a containing block for fixed
    // descendants, which would move every popover inside the guest layout.
    <div className="@container mb-4">
      <div
        role="alert"
        data-testid="guest-email-undeliverable"
        className="flex flex-col @md:flex-row @md:items-center gap-3 p-3 rounded-xl border outline-secondary"
      >
        <div className="flex-1">
          <p className="body-2-bold font-error">{t("undeliverableTitle")}</p>
          <p className="body-2-regular">
            {guest.emailUndeliverableAt
              ? t("undeliverableSince", {
                  date: new Date(guest.emailUndeliverableAt).toLocaleDateString(),
                })
              : t("undeliverableBody")}
          </p>
        </div>
        {/* SecondaryButton defaults to `w-full`, which as a flex item resolves
            to a basis of the WHOLE row: the text beside it then has nothing
            left to grow into and collapses to one word per line. `w-max` gives
            the button its intrinsic width back so the text column wins the
            free space; `w-full` is kept while the row is stacked, where the
            button should still span. Note `min-w-0` on the text would make
            this WORSE, not better — the min-content floor is what keeps the
            text readable at all. Left shrinkable on purpose: if a translation
            ever outgrows the row, a wrapped label is contained, whereas a
            `shrink-0` one pushes the whole notice past its panel. */}
        <SecondaryButton
          text={t("undeliverableClear")}
          size="small"
          width="w-full @md:w-max"
          testId="guest-email-undeliverable-clear"
          loading={clearing}
          disabled={clearing}
          onClick={handleClear}
        />
      </div>
    </div>
  );
};
